import { notification } from "antd";
import axios, { type AxiosError, type AxiosRequestConfig } from "axios";
import { mutate } from "swr";

import { config } from "utils/config";
import { replacer } from "utils/json";
import {
	isCompanyGuardRejection,
	isIntegrationAuthFailure,
	isSupportAccessEnded,
	isSupportAccessRefusal,
} from "./authError";
import { getIdToken, logout } from "./firebase";
import {
	ACT_AS_HEADER,
	clearActAsGrantId,
	getActAsGrantId,
} from "./supportAccess";

interface CustomConfig extends AxiosRequestConfig {
	_retry: boolean;
	/** Set once the act-as header has been dropped, so this cannot loop. */
	_supportAccessDropped?: boolean;
}

// Flag to prevent additional requests once authentication has failed
let isAuthFailed = false;

// Reset the auth failed flag (call this after successful login)
export const resetAuthFailedFlag = () => {
	isAuthFailed = false;
};

const exclude = [
	"auth/signin",
	"auth/signup",
	"auth/refresh",
	"auth/google-signin",
	"auth/reset-password",
	"auth/update-password",
];

// ----------------------------------------------------------------------

const api = axios.create({
	...config.api,
	transformRequest: [
		(data) => {
			// Don't transform FormData - let browser handle it with proper boundary
			if (data instanceof FormData) {
				return data;
			}
			// Idempotent: if we've already stringified (e.g. on a 401-retry where
			// axios re-runs transformRequest against the previously-transformed
			// `config.data`), don't double-stringify. Doing so produced bodies like
			// `"\"{\\\"transactions\\\":...\\\"}\""` and a server-side
			// "JSON Parse error: Unrecognized token" 400.
			if (typeof data === "string") {
				return data;
			}
			// Transform other data to JSON
			return JSON.stringify(data, replacer);
		},
	],
});

api.interceptors.request.use(async (config) => {
	// If authentication has failed, reject new requests immediately
	if (isAuthFailed) {
		return Promise.reject(
			new axios.Cancel("Authentication failed - request cancelled"),
		);
	}

	const accessToken = await getIdToken();
	if (accessToken) config.headers!.Authorization = `Bearer ${accessToken}`;

	// Support access. The Authorization header above is ALWAYS the staff
	// member's own token — this is an additional header naming the grant to act
	// under, never a substitute identity. The server resolves it against a grant
	// row it owns; a forged value buys nothing without that staff member's token.
	//
	// Sent on every request while a session is active, including the ones that
	// will be refused: a read-only session is enforced server-side, and hiding
	// the header on writes would make the client's picture of the session
	// disagree with the server's.
	const actAs = getActAsGrantId();
	if (actAs) config.headers![ACT_AS_HEADER] = actAs;

	// Names this client in the API's permission-decision log so web traffic can
	// be told apart from the extension and the assistant (EQ-2285). Identifies,
	// never authorizes. The ingress CORS allow-list must carry this header in
	// every environment first, or browsers reject every request.
	config.headers["X-Equility-Client"] =
		`web/${process.env.REACT_APP_VERSION || "unknown"}`;

	// Only set Content-Type to JSON if it's not FormData
	if (!(config.data instanceof FormData)) {
		config.headers["Content-Type"] = "application/json";
	}

	return config;
});

const fetcher = (url: string) => api.get(url).then((res) => res.data);

export { api, fetcher };

export const forceTokenRefresh = async (): Promise<string | null> => {
	try {
		const token = await getIdToken(true);
		return token || null; // Ensure null is returned if token is undefined
	} catch (error) {
		console.error("Error forcing token refresh:", error);
		return null;
	}
};

function readBlobText(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(String(reader.result ?? ""));
		reader.onerror = () => reject(reader.error);
		reader.readAsText(blob);
	});
}

// ----------------------------------------------------------------------

api.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		const originalRequest = error.config as CustomConfig;
		const status = error.response?.status;
		const url = originalRequest?.url || (originalRequest as any)?.config?.url;

		// A `responseType: "blob"` request gets its error body as a Blob too.
		// Decode a JSON one first, so the classification below (and the caller)
		// reads the real body: a blob CompanyGuard 401 would otherwise fall
		// through to refresh-then-logout (EQ-2542 EN-7).
		// Only a JSON (or untyped) Blob is read; a binary body is left alone.
		const blob = error.response?.data;
		if (
			blob instanceof Blob &&
			(blob.type === "" || blob.type.includes("json"))
		) {
			try {
				(error.response as { data: unknown }).data = JSON.parse(
					await readBlobText(blob),
				);
			} catch {
				console.warn("Could not decode a Blob error body as JSON", {
					url,
					status,
				});
			}
		}

		// Not every 401 means the Firebase session is bad. See utils/authError:
		// a CompanyGuard denial can't be fixed by refreshing the token, and a
		// lapsed QuickBooks token says nothing about the session at all. Both
		// must skip the refresh-then-logout path below — otherwise opening any
		// QBO-backed screen (e.g. the Invoice + Payments + Deposit dialog, which
		// fetches customers/items/accounts) logs the user out and wipes the SWR
		// cache instead of showing "reconnect QuickBooks".
		const errorBody = error.response?.data as
			| { message?: unknown; description?: unknown }
			| undefined;
		const companyGuardRejection = isCompanyGuardRejection(errorBody);
		const integrationAuthFailure = isIntegrationAuthFailure(errorBody);

		// A support session that ended is NOT a dead login. The staff member's
		// own Firebase token is fine — only the grant this tab was acting under
		// is gone, and no amount of refreshing it will bring the grant back. Left
		// to the generic path below, this 401 refreshed a good token, retried into
		// the identical refusal and logged the staff member out of their OWN
		// account; and because the dead grant id survives a reload in
		// sessionStorage, signing back in walked straight into it again.
		//
		// So: drop the header, forget the grant, and re-issue the request as
		// themselves. Recovery, not an error the operator has to act on.
		//
		// 🪤 GATED ON THE RESPONSE BODY, NOT ON `getActAsGrantId()`. A screen has
		// several requests in flight, and they all 401 together. If the condition
		// asked "is this tab still acting?", the first handler would clear the id
		// and every sibling would then read `null`, fall through to the generic
		// path, and retry with the dead header still sitting on its own config —
		// straight back into the logout this whole branch exists to prevent.
		if (
			status === 401 &&
			isSupportAccessRefusal(errorBody) &&
			!originalRequest._supportAccessDropped
		) {
			originalRequest._supportAccessDropped = true;
			// Single-threaded: whichever handler runs first sees the id and owns
			// the one-time work. The siblings only drop their own header.
			const isFirstToNotice = getActAsGrantId() !== null;
			clearActAsGrantId();
			if (originalRequest.headers)
				delete originalRequest.headers[ACT_AS_HEADER];

			if (isFirstToNotice)
				notification.warning({
					message: isSupportAccessEnded(errorBody)
						? "Support session ended"
						: "Support session is no longer valid",
					description:
						"You are back in your own account. Anything you do from here is as you, not as the customer.",
					duration: 8,
				});

			// Every cached response below belongs to the CUSTOMER, and the tab is
			// no longer in their session. Revalidating rather than merely dropping
			// it keeps the screen from rendering one identity's data under the
			// other's — the same reason the logout path wipes the cache.
			if (isFirstToNotice) mutate(() => true, undefined, { revalidate: true });

			// Back through `api`, not raw axios, so the request interceptor runs
			// again — it will not re-add the header now that the id is cleared.
			return api(originalRequest);
		}

		if (
			status === 401 &&
			!originalRequest._retry &&
			!companyGuardRejection &&
			!integrationAuthFailure
		) {
			originalRequest._retry = true;
			const accessToken = await getIdToken(true);
			if (accessToken) {
				originalRequest.headers!.Authorization = `Bearer ${accessToken}`;
				return axios(originalRequest);
			}
		}

		if (status === 401 && originalRequest._retry && !integrationAuthFailure) {
			// Set flag to prevent additional requests from firing
			isAuthFailed = true;
			logout();
			// Clear all SWR cache to prevent stale data
			mutate(() => true, undefined, { revalidate: false });
		}

		if (url && exclude.find((v) => url.includes(v)))
			return Promise.reject(error);

		if (status === 403) return Promise.reject(error);

		return Promise.reject(error);
	},
);

/**
 * Append `companyId=N` to a URL while preserving any existing query string.
 * The reconciliation/rule API requires `companyId` on every endpoint that
 * carries a UUID — CompanyGuard checks the user's access to the company,
 * and the service layer cross-checks the entity's `application_id` against
 * the same value (see equility-api ADR / security audit 2026-05-06).
 */
export function withCompanyId(
	url: string,
	companyId: number | string | null | undefined,
): string {
	if (companyId === null || companyId === undefined || companyId === "")
		return url;
	const separator = url.includes("?") ? "&" : "?";
	return `${url}${separator}companyId=${encodeURIComponent(String(companyId))}`;
}
